import React from 'react';

import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';

import ActionCell, { ActionConfig } from 'components/Grid/components/ActionCell';
import { useStore } from 'utils/hooks/store';
import { getEntityTranslation } from 'stores/EntityDetails/utils';

import { canDeleteEntity } from '../deletePermissions';
import { entityNameField } from '../entityFields';

import { EntityActionCellProps } from './types';

const EntityActionCell: React.FunctionComponent<EntityActionCellProps> = ({
  rowData,
  actions: { refreshData },
  store: { entityType: type, detailsPath, delete: deleteEntity },
}) => {
  const { uuid } = rowData;
  const recordName = rowData[entityNameField(type)];
  const navigate = useNavigate();
  const { notification, session } = useStore();

  const { singular } = getEntityTranslation[type];

  const handleEdit = (): void => {
    navigate(detailsPath(uuid));
  };

  const handleDelete = async (): Promise<void> => {
    const deleted = await deleteEntity(uuid);
    if (deleted) {
      notification.enqueueSuccessSnackbar(`${singular} deleted successfully`);
      await refreshData();
    } else {
      notification.enqueueErrorSnackbar(`The ${singular} could not be deleted. Something went wrong.`);
    }
  };

  const canDelete = canDeleteEntity(type, session.user);

  const actions: ActionConfig[] = [
    {
      id: 'edit-button',
      label: session?.user?.readOnly ? 'View' : 'Edit',
      onClick: handleEdit,
    },
    {
      id: 'remove-button',
      label: 'Delete',
      onClick: handleDelete,
      visible: canDelete,
      confirmation: {
        type: 'simple',
        title: `Delete ${singular}`,
        message: `Are you sure you want to delete this ${singular.toLowerCase()}?`,
        confirmButtonLabel: 'Delete',
      },
    },
  ];

  return <ActionCell actions={actions} recordName={typeof recordName === 'string' ? recordName : undefined} />;
};

export default observer(EntityActionCell);
